import React, { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import useAuthAxios from "../../../../utils/baseAxios";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { API } from "../../../../constants/api";
import { errorMessage } from "../../../../utils/errorMessage";
import { IoMdArrowDropdown } from "react-icons/io";
import { formatAmount } from "../../../../utils/formatTime";
import GetRates from "../../../../components/utils/GetRates";
import { useUser } from "../../../../context/user-context";
import { useDebounce } from "use-debounce";
import ClipLoader from "react-spinners/ClipLoader";
import { FaCheckCircle } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import { FaCaretDown } from "react-icons/fa";
import ProviderAvatar from "../../../../components/ProviderAvatar";
import PaymentSourceSelect from "../PaymentSourceSelect";
import { NAIRA_WALLET_ENABLED } from "../../../../constants/constants";

const Betting = ({
  setOpenBetting,
  setBetId,
  setCustomerId,
  setBetAmount,
  betAmount,
  customerId,
  betId,
  providerName,
  setProviderName,
  setCustomerName,
  customerName,
  setOpenBettingPreview,
  paymentSource,
  setPaymentSource,
}: any) => {
  const axiosInstance = useAuthAxios();
  const { userDetails } = useUser();
  const [debouncedCustomerId] = useDebounce(customerId, 800);
  const [openPlans, setOpenPlans] = useState<boolean>(false);
  const walletBalances = userDetails?.user.deexWalletBalances;
  const usdtBalance = walletBalances?.usdt ?? 0;
  const ngnBalance = walletBalances?.ngn ?? 0;
  const usdtRate = GetRates("USDT");
  const [disabled, setDisabled] = useState<boolean>(false);

  const isBalanceSufficient =
    paymentSource === "NGN"
      ? Number(betAmount) <= Number(ngnBalance)
      : Number(betAmount) / Number(usdtRate) <= Number(usdtBalance);
  const [displayAmount, setDisplayAmount] = useState<string>("");
  // Whole naira only — the fund endpoint's `amount` is an integer.
  const isNumber = (str: string) => /^\d*$/.test(str);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas for parsing
    if (isNumber(value)) {
      setBetAmount(value); // Store raw amount
      setDisplayAmount(value); // Temporarily show raw input while typing
    }
  };

  // Debounce formatting to run when the user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      if (betAmount) {
        const formattedValue = Number(betAmount).toLocaleString("en-US");
        setDisplayAmount(formattedValue); // Set formatted value after debounce
      }
    }, 300); // 500ms debounce time
    return () => clearTimeout(handler);
  }, [betAmount]);

  const getProviders = async () => {
    const response = await axiosInstance.get(API.getBettingProviders);
    return response.data;
  };

  const { data: providers, error: error1 } = useQuery({
    queryKey: ["get-betting-providers"],
    queryFn: getProviders,
    retry: 1,
  });

  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);
  // Nomba's provider list has no `active` flag — only hide ones explicitly marked inactive.
  const activeProviders = (providers?.data ?? []).filter(
    (provider: { active?: boolean }) => provider.active !== false
  );

  //confirm customerId

  const confirmId = async () => {
    const response = await axiosInstance.get(
      API.validateCustomer(betId, debouncedCustomerId)
    );
    return response.data;
  };

  const {
    data: isconfirmed,
    error: error4,
    isLoading: loading,
  } = useQuery({
    queryKey: ["validate-ID", betId, debouncedCustomerId],
    queryFn: confirmId,
    retry: 1,
    enabled: !!betId && !!debouncedCustomerId,
  });

  useEffect(() => {
    if (error4) {
      const newError = error4 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error4]);
  // Response: { status: "success", data: { customerName, customer_name, customerId, customer_id } }
  const validatedName =
    isconfirmed?.status === "success"
      ? isconfirmed?.data?.customerName ?? isconfirmed?.data?.customer_name
      : undefined;
  const isValidated = Boolean(validatedName);
  useEffect(() => {
    setCustomerName(validatedName ?? "");
  }, [validatedName, setCustomerName]);
  useEffect(() => {
    if (!customerId || !betAmount || !isBalanceSufficient || !isValidated) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [betAmount, customerId, isBalanceSufficient, isValidated]);

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setBetAmount("");
              setCustomerId("");
              setBetId("");
              setProviderName("");
              setCustomerName("");
              setOpenBetting(false);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[20px]">
            Betting
          </h4>
        </div>
        <div className="w-full mt-10">
          <PaymentSourceSelect
            value={paymentSource}
            onChange={setPaymentSource}
          />
        </div>
        <div className="w-full mt-6 relative">
          <h4 className="text-[14px] font-bold text-gray-500 ">Provider</h4>
          <div
            onClick={() => {
              setOpenPlans(!openPlans);
            }}
            className="w-full cursor-pointer border border-[#EAEAEA] dark:border-gray-700 shadow text-gray-500 dark:text-gray-400  rounded-[4px] p-3 mt-3 flex justify-between items-center capitalize px-4 py-3 text-[14px] gap-4 "
          >
            {providerName ? (
              <div className="flex items-center gap-2">
                <ProviderAvatar name={providerName} size={24} />
                <span>{providerName}</span>
              </div>
            ) : (
              "Select provider"
            )}
            <FaCaretDown className=" pointer-events-none text-gray-900 dark:text-gray-200 text-[20px]" />
          </div>

          {openPlans && activeProviders.length > 0 && (
            <div className="w-full bg-gray-50 dark:bg-gray-800 p-4 h-[450px] overflow-auto rounded-xl absolute z-50 mt-2">
              {activeProviders.map((provider: any) => (
                <div
                  key={provider.id}
                  onClick={() => {
                    setBetId(provider.id);
                    setProviderName(provider.title);
                    setOpenPlans(false);
                  }}
                  className="p-4 border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent rounded-xl mt-2 cursor-pointer flex items-center gap-3"
                >
                  <ProviderAvatar
                    name={provider.title}
                    logoUrl={provider.logo_url ?? provider.logoUrl}
                  />
                  <h4 className="text-[14px] dark:text-gray-400 text-gray-500 font-medium">
                    {provider.title}
                  </h4>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6">
          <h4 className="text-[14px] font-bold text-gray-500 ">Customer ID</h4>
          <div className="w-full cursor-pointer border border-[#EAEAEA] dark:border-gray-700 shadow text-gray-700 dark:text-gray-300  rounded-[4px] p-3 mt-3 flex justify-between items-center capitalize px-4 py-3 text-[14px] gap-4 ">
            <input
              type="tel"
              placeholder="Enter customer ID"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="text-gray-700 dark:text-gray-300 bg-transparent  w-full text-[13px] text-left outline-none"
            />
            {loading ? (
              <ClipLoader color="#0B84CB" size={20} />
            ) : (
              <>
                {" "}
                {debouncedCustomerId &&
                  betId &&
                  (isconfirmed !== undefined || error4) && (
                    <>
                      {isValidated ? (
                        <FaCheckCircle className="text-green-500 text-[15px]" />
                      ) : (
                        <IoCloseCircle className="text-red-500 text-[15px]" />
                      )}
                    </>
                  )}
              </>
            )}

            {/* Show loader while loading */}
          </div>
          {customerName && (
            <div className="justify-between flex items-center">
              <h4 className="text-[12px] text-[#BE6B0A] font-bold mt-1">
                {customerName}
              </h4>
            </div>
          )}
        </div>
        {/* Amount Input */}
        <div className="w-full mt-6">
          <div className="flex justify-between items-center">
            <h4 className="text-[14px] font-bold text-gray-500 ">Amount</h4>
          </div>
          <div className="w-full flex justify-between items-center border border-[#EAEAEA] dark:border-gray-700 shadow  rounded-[4px] p-3 mt-3">
            <h4 className="text-[13px] font-bold text-black dark:text-white">
              NGN
            </h4>
            <div className="w-[40%]">
              <div className="w-full flex items-center justify-end text-gray-900 dark:text-gray-200">
                <span className="font-sora font-bold text-[18px]">₦</span>
                <input
                  type="text"
                  placeholder="0"
                  value={displayAmount}
                  onChange={handleChange}
                  style={{ width: `${Math.max(displayAmount.length, 4)}ch` }}
                  className="text-right text-[18px] font-semibold bg-transparent outline-none"
                />
              </div>
              {paymentSource === "USDT" && (
                <h4 className="text-right mt-1 text-[12px] font-medium text-gray-500 pr-2">
                  {displayAmount
                    ? `${formatAmount(betAmount / usdtRate)} USDT`
                    : "0.00 USDT"}
                </h4>
              )}
            </div>
          </div>
          <div className="justify-between flex items-center">
            {NAIRA_WALLET_ENABLED && (
              <h4 className="text-[12px] text-[#BE6B0A] font-bold mt-1">
                Balance:{" "}
                {paymentSource === "NGN"
                  ? `₦${formatAmount(ngnBalance)}`
                  : `${formatAmount(usdtBalance)} USDT`}
              </h4>
            )}
            {!isBalanceSufficient && betAmount && (
              <h4 className="text-red-600 font-bold mt-1 text-[10px] ">
                Insufficient funds
              </h4>
            )}
          </div>
        </div>
        <button
          disabled={disabled}
          onClick={() => {
            setOpenBetting(false);
            setOpenBettingPreview(true);
          }}
          className={
            disabled
              ? "mt-12 bg-gray-400 flex justify-center items-center text-gray-200 text-[15px] font-medium h-[48px] w-full rounded-xl"
              : "mt-12 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
          }
        >
          Proceed
        </button>
      </div>
    </div>
  );
};

export default Betting;
